import {getTestProps} from '@benchling/js-test-utils';
import {ActionIcon, Box, Button, createStyles, Group, Text, Tooltip} from '@mantine/core';
import React, {useCallback} from 'react';

import {AgentApprovalCategory, AgentApprovalResponse} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as ChatActions from 'llm/chat-library/ChatActions';
import {buildAgentApprovalDecision} from 'llm/chat-library/ChatDAO';
import {ChatDraftStatus, getOrCreateDraftState} from 'llm/chat-library/ChatDraftState';
import {assertNever} from 'util/Types';

/**
 * Tooltip for the "Allow" button, shown when approving switches this chat to Full actions mode —
 * i.e. whenever approval grants chat-scoped WRITE_ACTIONS (regular write requests from Default
 * mode, and admin requests from Default mode, which per BNCH-167792 also grant chat-scoped writes).
 */
const SWITCH_TO_FULL_ACTIONS_TOOLTIP =
  'Approving will execute the proposed plan and switch this chat to Full actions mode, where ' +
  'editing is enabled.';

/**
 * Tooltip for the "Allow once" button, shown for an admin-action request while already in Full
 * actions mode, where approval does not change the chat's mode.
 */
const ALLOW_ONCE_TOOLTIP =
  'Approving will execute the proposed plan. Admin actions require approval each time.';

/** Tooltip on the "Deny" button explaining that a typed reply is an alternative to denying. */
const DENY_TOOLTIP = 'Click to deny, or send a message to explain what to do instead.';

/** Static indicator text shown once the request has been answered, in place of the buttons. */
function getAnsweredIndicatorText(response: AgentApprovalResponse): string {
  switch (response) {
    case AgentApprovalResponse.APPROVED_THIS_TURN:
    case AgentApprovalResponse.APPROVED_THIS_CHAT:
      return 'Approval granted';
    case AgentApprovalResponse.EXPLICITLY_DENIED:
    case AgentApprovalResponse.IMPLICITLY_DENIED:
      return 'Approval not granted';
    default:
      assertNever(response);
      return 'Approval not granted';
  }
}

interface ApprovalPresentation {
  approveLabel: string;
  approveResponse: AgentApprovalResponse;
  tooltip: string;
}

/**
 * Consolidates the three underlying request shapes into the two presentations Alan specified
 * (BNCH-168702): approving a request that grants (or re-grants) chat-scoped WRITE_ACTIONS always
 * reads as "Allow" / switch-to-Full-actions-mode copy, regardless of whether the request itself is
 * a WRITE_ACTIONS request (C1) or an ADMIN_ACTIONS request answered from Default mode (C3, which
 * per BNCH-167792 also grants chat-scoped writes on approval). Only an ADMIN_ACTIONS request
 * answered while already in Full actions mode (C2) reads as "Allow once".
 *
 * This intentionally keys on `hasChatScopedWriteApproval` (i.e. WRITE_ACTIONS), not on any
 * folder-aware display selector, so a folder-scoped chat requesting an ordinary write is never
 * mistaken for an admin-only request.
 */
function getApprovalPresentation(
  category: AgentApprovalCategory,
  hasChatScopedWriteApproval: boolean
): ApprovalPresentation {
  switch (category) {
    case AgentApprovalCategory.WRITE_ACTIONS:
      return {
        approveLabel: 'Allow',
        approveResponse: AgentApprovalResponse.APPROVED_THIS_CHAT,
        tooltip: SWITCH_TO_FULL_ACTIONS_TOOLTIP,
      };
    case AgentApprovalCategory.ADMIN_ACTIONS:
      return {
        approveLabel: hasChatScopedWriteApproval ? 'Allow once' : 'Allow',
        approveResponse: AgentApprovalResponse.APPROVED_THIS_TURN,
        tooltip: hasChatScopedWriteApproval ? ALLOW_ONCE_TOOLTIP : SWITCH_TO_FULL_ACTIONS_TOOLTIP,
      };
    default:
      assertNever(category);
      return {
        approveLabel: 'Allow',
        approveResponse: AgentApprovalResponse.APPROVED_THIS_CHAT,
        tooltip: SWITCH_TO_FULL_ACTIONS_TOOLTIP,
      };
  }
}

const useStyles = createStyles((theme) => ({
  // A distinct, system-owned card that sits at the bottom of the agent's approval-request message,
  // inside its border. The different background separates it from the agent's message content.
  card: {
    marginTop: theme.spacing.md,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    borderRadius: theme.radius.sm,
    backgroundColor: theme.colors.blue[0],
    border: `1px solid ${theme.colors.blue[2]}`,
  },
}));

/**
 * The distinct system card appended to the bottom of an assistant write-approval request message.
 *
 * While unanswered and not readonly, it shows approval and denial actions whose copy reflects the
 * request category and current chat-scoped WRITE_ACTIONS state. ADMIN_ACTIONS always submits
 * APPROVED_THIS_TURN; from Default mode, the server also persists WRITE_ACTIONS so the chat switches
 * to Full actions mode. Once answered, the buttons are replaced with a static "Approval granted" /
 * "Approval not granted" indicator so stale requests in history cannot be re-answered.
 */
export default function AgentApprovalCard({
  category,
  chatSessionId,
  requestEventId,
  answeredResponse,
  readonly,
  hasChatScopedWriteApproval,
}: {
  category: AgentApprovalCategory;
  chatSessionId: string;
  /**
   * ID of the assistant display message chat event this card belongs to (it carried the approval
   * request). Recorded on the submitted decision so it links back to the request it answers.
   */
  requestEventId: string;
  /** When set, the request has been answered; show a static indicator instead of the buttons. */
  answeredResponse: AgentApprovalResponse | null;
  readonly: boolean;
  /** Whether WRITE_ACTIONS is already granted for the rest of this chat. */
  hasChatScopedWriteApproval: boolean;
}): JSX.Element {
  const {classes} = useStyles();
  const approvalPresentation = getApprovalPresentation(category, hasChatScopedWriteApproval);
  const submitDecision = useCallback(
    (approvalResponse: AgentApprovalResponse) => {
      const draft = getOrCreateDraftState(chatSessionId);
      // Submit whatever the user has typed (if anything) alongside the decision, rather than
      // discarding their in-progress draft. Usually the composer is empty and only the decision is
      // recorded on the user message.
      ChatActions.submitDraftMessage(chatSessionId, {
        draft: {
          ...draft,
          status: ChatDraftStatus.DRAFT,
        },
        createParams: {
          agentApprovalDecision: buildAgentApprovalDecision(approvalResponse, requestEventId, category),
        },
      });
    },
    [category, chatSessionId, requestEventId]
  );
  const handleApprove = useCallback(() => {
    submitDecision(approvalPresentation.approveResponse);
  }, [approvalPresentation.approveResponse, submitDecision]);
  const handleDeny = useCallback(() => {
    submitDecision(AgentApprovalResponse.EXPLICITLY_DENIED);
  }, [submitDecision]);

  return (
    <Box className={classes.card} {...getTestProps({component: 'AgentApprovalCard', element: 'root'})}>
      <Group position="apart" spacing="xs" noWrap={true}>
        <Group spacing={6} noWrap={true}>
          <Text size="sm" weight={600}>
            Approval required
          </Text>
          <Tooltip label={approvalPresentation.tooltip} multiline={true} width={280} withArrow={true}>
            <ActionIcon
              size="xs"
              radius="xl"
              variant="light"
              color="blue"
              aria-label="What are actions?"
              {...getTestProps({component: 'AgentApprovalCard', element: 'info'})}
            >
              <Text size="xs" weight={700}>
                ?
              </Text>
            </ActionIcon>
          </Tooltip>
        </Group>
        {answeredResponse != null ? (
          <Text
            size="sm"
            weight={600}
            color="dimmed"
            {...getTestProps({component: 'AgentApprovalCard', element: 'answered-indicator'})}
          >
            {getAnsweredIndicatorText(answeredResponse)}
          </Text>
        ) : (
          !readonly && (
            <Group spacing="xs" noWrap={true}>
              <Tooltip label={DENY_TOOLTIP} multiline={true} width={240} withArrow={true}>
                <Button
                  size="xs"
                  variant="tertiary"
                  onClick={handleDeny}
                  {...getTestProps({component: 'AgentApprovalCard', element: 'deny'})}
                >
                  Deny
                </Button>
              </Tooltip>
              <Button
                size="xs"
                variant="primary"
                onClick={handleApprove}
                {...getTestProps({component: 'AgentApprovalCard', element: 'approve'})}
              >
                {approvalPresentation.approveLabel}
              </Button>
            </Group>
          )
        )}
      </Group>
    </Box>
  );
}
